/**
 * Subscription Page - Lighthouse Pricing Tiers
 * Display pricing plans and feature comparisons
 */

import React from 'react';
import { Link } from 'react-router-dom';

const pricingTiers = [
    {
        name: 'Basic',
        price: '$99',
        period: 'per month + taxes',
        description: 'Perfect for small businesses getting started',
        color: '#4a90a4',
        recommended: false,
        features: [
            { text: 'Up to 100 conversations/month', included: true },
            { text: 'Web chat widget', included: true },
            { text: 'Basic AI responses', included: true },
            { text: 'Email support', included: true },
            { text: 'SMS integration', included: false },
            { text: 'Custom branding', included: false },
            { text: 'Analytics dashboard', included: false },
            { text: 'Priority support', included: false }
        ],
        cta: 'Get Started'
    },
    {
        name: 'Pro',
        price: '$499',
        period: 'per month + taxes',
        description: 'For growing businesses that need more',
        color: '#2c5f7d',
        recommended: true,
        features: [
            { text: 'Up to 2,500 conversations/month', included: true },
            { text: 'Web chat widget', included: true },
            { text: 'Advanced AI with Augure — Sovereign Canadian AI', included: true },
            { text: 'Email support', included: true },
            { text: 'SMS integration', included: true },
            { text: 'Custom branding', included: true },
            { text: 'Analytics dashboard', included: true },
            { text: 'Priority support', included: false }
        ],
        cta: 'Start Pro Trial'
    },
    {
        name: 'Custom',
        price: 'Let\'s chat!',
        period: 'based on needs',
        description: 'Unlimited scale with dedicated support',
        color: '#1a2332',
        recommended: false,
        features: [
            { text: 'Unlimited conversations', included: true },
            { text: 'Web chat widget', included: true },
            { text: 'Advanced AI with multiple LLM options', included: true },
            { text: 'Email support', included: true },
            { text: 'SMS integration', included: true },
            { text: 'Custom branding', included: true },
            { text: 'Analytics dashboard', included: true },
            { text: 'Priority support & SLA', included: true }
        ],
        cta: 'Contact Sales'
    }
];

function SubscriptionPage() {
    return (
        <div className="subscription-page">
            {/* Navbar */}
            <nav className="navbar navbar-dark bg-lighthouse-gradient">
                <div className="container">
                    <Link to="/" className="navbar-brand mb-0 h1">
                        ⚓ Lighthouse
                    </Link>
                    <div className="d-flex gap-3">
                        <Link to="/home" className="nav-link text-white">Home</Link>
                        <Link to="/subscription" className="nav-link text-white">Pricing</Link>
                        <Link to="/" className="nav-link text-white-50">Demos</Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="hero bg-lighthouse-gradient text-white py-5">
                <div className="container text-center py-5">
                    <h1 className="display-3 fw-bold mb-4">
                        Simple, Transparent Pricing
                    </h1>
                    <p className="lead mb-4">
                        Choose the plan that's right for your business
                    </p>
                    <p className="text-white-50">
                        All plans include our core AI-powered customer assistance features
                    </p>
                    <p className="text-white-50 mt-3">
                        <small>* One-time setup and onboarding fee of $499 applies to all plans</small>
                    </p>
                </div>
            </section>

            {/* Pricing Cards Section */}
            <section className="pricing py-5">
                <div className="container">
                    <div className="row g-4 align-items-stretch">
                        {pricingTiers.map((tier, index) => (
                            <div key={index} className="col-lg-4">
                                <div
                                    className={`card card-hover h-100 border-0 shadow-lg ${tier.recommended ? 'border-primary' : ''}`}
                                    style={{
                                        position: 'relative',
                                        transform: tier.recommended ? 'scale(1.05)' : 'scale(1)',
                                        transition: 'transform 0.3s ease'
                                    }}
                                >
                                    {tier.recommended && (
                                        <div
                                            className="position-absolute top-0 start-50 translate-middle"
                                            style={{ zIndex: 1 }}
                                        >
                                            <span
                                                className="badge bg-primary px-4 py-2"
                                                style={{ fontSize: '0.9rem' }}
                                            >
                                                ⭐ RECOMMENDED
                                            </span>
                                        </div>
                                    )}

                                    <div className="card-body p-5">
                                        <div className="text-center mb-4">
                                            <h3 className="fw-bold mb-2">{tier.name}</h3>
                                            <div className="mb-3">
                                                <span
                                                    className="display-4 fw-bold"
                                                    style={{ color: tier.color }}
                                                >
                                                    {tier.price}
                                                </span>
                                                {tier.price !== 'Custom' && (
                                                    <span className="text-muted">/{tier.period}</span>
                                                )}
                                            </div>
                                            <p className="text-muted">{tier.description}</p>
                                        </div>

                                        <ul className="list-unstyled mb-4">
                                            {tier.features.map((feature, fIndex) => (
                                                <li key={fIndex} className="mb-3 d-flex align-items-start">
                                                    <span
                                                        className="me-2"
                                                        style={{
                                                            fontSize: '1.2rem',
                                                            color: feature.included ? '#28a745' : '#dc3545'
                                                        }}
                                                    >
                                                        {feature.included ? '✓' : '✗'}
                                                    </span>
                                                    <span
                                                        className={feature.included ? 'text-dark' : 'text-muted'}
                                                        style={{
                                                            textDecoration: feature.included ? 'none' : 'line-through'
                                                        }}
                                                    >
                                                        {feature.text}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>

                                        <div className="text-center">
                                            <button
                                                className={`btn btn-lg w-100 ${tier.recommended ? 'btn-primary' : 'btn-outline-primary'}`}
                                                style={{
                                                    backgroundColor: tier.recommended ? tier.color : 'transparent',
                                                    borderColor: tier.color,
                                                    color: tier.recommended ? 'white' : tier.color
                                                }}
                                            >
                                                {tier.cta}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="faq bg-lighthouse-light py-5">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3">Frequently Asked Questions</h2>
                        <p className="text-muted">
                            Everything you need to know about Lighthouse pricing
                        </p>
                    </div>

                    <div className="row">
                        <div className="col-lg-8 mx-auto">
                            <div className="accordion" id="faqAccordion">
                                <div className="accordion-item border-0 shadow-sm mb-3">
                                    <h3 className="accordion-header">
                                        <button
                                            className="accordion-button fw-bold"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#faq1"
                                        >
                                            Can I switch plans later?
                                        </button>
                                    </h3>
                                    <div id="faq1" className="accordion-collapse collapse show" data-bs-parent="#faqAccordion">
                                        <div className="accordion-body text-muted">
                                            Yes! You can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle. If you upgrade mid-cycle, you'll receive a prorated credit for your current plan.
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item border-0 shadow-sm mb-3">
                                    <h3 className="accordion-header">
                                        <button
                                            className="accordion-button collapsed fw-bold"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#faq2"
                                        >
                                            What happens if I exceed my conversation limit?
                                        </button>
                                    </h3>
                                    <div id="faq2" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                        <div className="accordion-body text-muted">
                                            We'll notify you via email when you reach 80% and 95% of your conversation limit. You can upgrade to a higher tier or purchase additional conversation credits at $0.50 per conversation. Your service will continue uninterrupted.
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item border-0 shadow-sm mb-3">
                                    <h3 className="accordion-header">
                                        <button
                                            className="accordion-button collapsed fw-bold"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#faq3"
                                        >
                                            Is there a free trial?
                                        </button>
                                    </h3>
                                    <div id="faq3" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                        <div className="accordion-body text-muted">
                                            Let's chat!
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item border-0 shadow-sm mb-3">
                                    <h3 className="accordion-header">
                                        <button
                                            className="accordion-button collapsed fw-bold"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#faq4"
                                        >
                                            What's included in the $499 setup fee?
                                        </button>
                                    </h3>
                                    <div id="faq4" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                        <div className="accordion-body text-muted">
                                            The one-time setup fee covers complete onboarding including: custom AI training on your business knowledge base, chat widget installation and customization, SMS number provisioning (if applicable), team training session, and dedicated onboarding specialist support.
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item border-0 shadow-sm mb-3">
                                    <h3 className="accordion-header">
                                        <button
                                            className="accordion-button collapsed fw-bold"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#faq5"
                                        >
                                            Are taxes included in the pricing?
                                        </button>
                                    </h3>
                                    <div id="faq5" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                        <div className="accordion-body text-muted">
                                            No, applicable Canadian taxes will be added to your subscription. You'll see the exact tax amount before confirming your subscription.
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item border-0 shadow-sm mb-3">
                                    <h3 className="accordion-header">
                                        <button
                                            className="accordion-button collapsed fw-bold"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#faq6"
                                        >
                                            How does billing work?
                                        </button>
                                    </h3>
                                    <div id="faq6" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                        <div className="accordion-body text-muted">
                                            All plans are billed monthly in advance. We accept all major credit cards and Interac e-transfers for annual subscriptions. You'll receive an invoice 3 days before each billing cycle.
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item border-0 shadow-sm mb-3">
                                    <h3 className="accordion-header">
                                        <button
                                            className="accordion-button collapsed fw-bold"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#faq7"
                                        >
                                            Can I cancel anytime?
                                        </button>
                                    </h3>
                                    <div id="faq7" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                        <div className="accordion-body text-muted">
                                            Yes, you can cancel your subscription at any time with no penalties. Your service will continue until the end of your current billing period. We'll provide an export of all your conversation data and analytics before your account closes.
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item border-0 shadow-sm mb-3">
                                    <h3 className="accordion-header">
                                        <button
                                            className="accordion-button collapsed fw-bold"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#faq8"
                                        >
                                            Is my customer data secure?
                                        </button>
                                    </h3>
                                    <div id="faq8" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                        <div className="accordion-body text-muted">
                                            Absolutely. All data is encrypted at rest and in transit using industry-standard AES-256 encryption. We're SOC 2 Type II compliant and GDPR ready. Your data is stored in secure, multi-tenant isolated databases with automatic daily backups.
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item border-0 shadow-sm mb-3">
                                    <h3 className="accordion-header">
                                        <button
                                            className="accordion-button collapsed fw-bold"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#faq9"
                                        >
                                            What integrations are available?
                                        </button>
                                    </h3>
                                    <div id="faq9" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                        <div className="accordion-body text-muted">
                                            Lighthouse integrates with Twilio for SMS, supports webhook integrations for CRM systems, and provides a REST API for custom integrations. Custom plan customers can request dedicated integrations with platforms like Salesforce, HubSpot, or Zendesk.
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item border-0 shadow-sm">
                                    <h3 className="accordion-header">
                                        <button
                                            className="accordion-button collapsed fw-bold"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#faq10"
                                        >
                                            How do I get a Custom plan?
                                        </button>
                                    </h3>
                                    <div id="faq10" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                        <div className="accordion-body text-muted">
                                            Contact our sales team at sales@topsailsoftware.com or click "Contact Sales" above. We'll schedule a consultation to understand your needs and create a custom package with unlimited conversations, dedicated support, SLA guarantees, and any specialized features you require.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta bg-lighthouse-gradient text-white py-5">
                <div className="container text-center py-4">
                    <h2 className="fw-bold mb-3">Still Have Questions?</h2>
                    <p className="lead mb-4">
                        Our team is here to help you find the perfect plan
                    </p>
                    <div className="d-flex gap-3 justify-content-center">
                        <Link to="/" className="btn btn-light btn-lg">
                            Try a Demo
                        </Link>
                        <a href="mailto:sales@topsailsoftware.com" className="btn btn-outline-light btn-lg">
                            Contact Sales
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-dark text-white-50 py-4">
                <div className="container text-center">
                    <p className="mb-0">
                        Built with ⚓ by <Link to="/home" className="text-white text-decoration-none">Topsail Software Inc.</Link>
                    </p>
                </div>
            </footer>
        </div>
    );
}

export default SubscriptionPage;
